import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { ZohoService } from '@lmc-app/core/services/zoho.service';
import { Agency } from '@lmc-app/shared/interface/agency.interface';
import { ModuleFieldPicklist } from '@lmc-app/shared/interface/module-field-picklist';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BehaviorSubject, finalize, Subject, Subscription, switchMap } from 'rxjs';
import { getPrimaryAgreementFields } from '../form-interfaces/agreements-primary-interface';
import { getCoAppAgreementFields } from '../form-interfaces/agreements-coapp-interface';
import { getFundedDetailsAgreementFields } from '../form-interfaces/agreements-funded-details-interface';
import { AgreementsFormOptions } from '../form-interfaces/agreements-form-options.interface';
import { callLoader, flagMissingControls, getMissingFields, hasClearedRequiredFields, hasRequiredContactChanges, localizeDateFields, mapRecipientForEnvelopeUpdate, MissingField, revealMissingFields } from '../../helper-functions/docusign-utils';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import dayjs from 'dayjs';
import { ListenerService } from '@lmc-app/core/services/listener.service';
import { getRecipientList } from '../form-interfaces/agreements-recipient-interface';
import { Recipient } from '@lmc-app/summary/docusign/interface/docusign-envelope-recipients-interface';
import { AGREEMENT_TEMPLATE_ROW_ID, PROVINCE_NAME_TO_CODE } from '../../constants/docusign-constants';

@Component({
  selector: 'app-agreement-content-update',
  templateUrl: './agreement-content-update.component.html',
  styleUrls: ['./agreement-content-update.component.scss']
})
export class AgreementContentUpdateComponent implements OnInit, OnChanges, OnDestroy {
  @Input() application!: any;
  @Input() currentUser!: string;
  @Input() envelopeId!: string;
  @Input() showRecipients: boolean = false;
  @Input() isDraftReady: boolean = false;   // True once the preview PDF has loaded
  @Output() applicationChange = new EventEmitter();
  @Output() recipientsClose = new EventEmitter<void>();

  private formOptions: Partial<AgreementsFormOptions>;
  formlyForm = new FormGroup({});   // Initialize the FormGroup correctly
  formlyFields: FormlyFieldConfig[] = [];   // separate variable for the field configuration array
  recipientForm = new FormGroup({});   // Recipients render in their own view, outside the accordion
  recipientFields: FormlyFieldConfig[] = [];
  dataModel = {};   // Holds form data
  missingFields: MissingField[] = [];   // Required fields with no valid value

  // Blocks Update All: saving would wipe a required value that was there before
  get hasClearedRequiredFields(): boolean {
    return hasClearedRequiredFields(this.missingFields, this.originalData);
  }
  private maxCalendarDate = new Date(new Date().getFullYear() - 1, 11, 31);
  

  isScrolled: boolean = false;
  isSahaApp: boolean;
  originalData:any;
  pdfSource = null;  

      
  private saveTrigger$ = new Subject<string>(); 
  private agencies$ = new BehaviorSubject<Agency[]>([]);
  private creditors$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
  private salesAgents$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
  private provinces$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);

  readonly templateChangeData = ['fullprovince'];
  
  private readonly _bottomSheet = inject(MatBottomSheet);

  updateAvailable: boolean = false;
  isSavingChanges: boolean = false;
  private recipientsReordered = false;
  
  private subs = new Subscription();

  constructor(
    private _docusignService: DocusignService,  
    private readonly apiService: LegacyApiService,
    private readonly zohoService: ZohoService,     
    private _listenerService: ListenerService,   
  ){}

  private hasInitialized = false;

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.application?.currentValue){
      this.application = changes.application?.currentValue;
    }
    if(changes.currentUser?.currentValue){
      this.currentUser = changes.currentUser?.currentValue;
    }
    if(changes.envelopeId?.currentValue){
      this.envelopeId = changes.envelopeId?.currentValue;
    }
    const envelopeChanged = !!changes.envelopeId?.previousValue && changes.envelopeId.previousValue !== this.envelopeId;
    const updatingChanged = !!changes.isEnvelopeUpdating && !changes.isEnvelopeUpdating.firstChange;
    if (envelopeChanged || updatingChanged) {
      this.reloadRecipientList();
    }

    if (!this.hasInitialized && this.application && this.currentUser) {
      this.hasInitialized = true;
      this.initializeComponent();
    }
  }

  ngOnInit(): void {
    this.loadDropdownOptions();

    this.subs.add(
      this._listenerService.on<string>('agreementUpdateCalc').subscribe((calcModel) => {
        if (calcModel) {
          this._listenerService.emit('agreementPreviewLoading', true);
          const recipients = calcModel?.['recipients'] ;
          const applicationId = calcModel?.['originalData']?.['id'];
          this._docusignService.getUpdatedZohoApplication(applicationId).subscribe(async res => {  
            if(res){
              this.application = res?.data?.[0] ;
              await this.normalizeAgreementData(this.application);
              if (recipients) {
                this.dataModel['recipients'] = recipients;
              }
              this.recreateAgreementDocument(this.dataModel, this.currentUser);
            }      
          })
        }
      })
    );
  }

  private async initializeComponent(): Promise<void> {
    await this.normalizeAgreementData(this.application);
    this.initializeFormConfig();
    setTimeout(() => revealMissingFields(this.formlyFields, this.missingFields));   // Wait for formly to build the controls
    this.formlyForm.valueChanges.subscribe(() => {
      this.syncProvinceCode();
      // Recompute so filled fields drop off and cleared required fields block Send again
      this.missingFields = getMissingFields(this.dataModel);
      flagMissingControls(this.formlyFields, this.missingFields);
      this.updateAvailable = this.recipientsReordered
        || Object.keys(this.getChangedFields(this.originalData, this.dataModel)).length > 0;
    });
  }

  // Update Provinces on three places
  private syncProvinceCode(): void {
    const fullProvinceName = this.dataModel?.['FullProvince']?.value;
    const code = PROVINCE_NAME_TO_CODE[fullProvinceName];
    if (!code) return;

    ['AddressProvince', 'Province'].forEach((key) => {
      if (this.dataModel?.[key] && this.dataModel[key].value !== code) {
        this.dataModel[key].value = code;
      }
    });

    if (this.dataModel?.['originalData']) {
      this.dataModel['originalData'].Province = code;
      this.dataModel['originalData'].Province1 = fullProvinceName;
    }
  }

  async updateNormalizeAgreementData(application:any): Promise<any>{
    return new Promise((resolve, reject) => {
      const sub = this._docusignService.normalizeAgreementData(application, this.currentUser).subscribe({
        next: (data) => {
          sub.unsubscribe(); // Clean up memory
          resolve(data?.[0]);     // Send data out of the promise
        },
        error: (err) => reject(err)
      });
    });
  }

  async normalizeAgreementData(application:any){
    return new Promise<void>((resolve, reject) => {
       this._docusignService.normalizeAgreementData(application, this.currentUser).subscribe({
        next: (res) => {
          this.dataModel = {};
          localizeDateFields(res?.[0]);
          this.isSahaApp = res?.[0]?.HasSaha ;
          this.originalData = JSON.parse(JSON.stringify(res?.[0] ?? {})); // deep clone so nested field objects aren't shared with dataModel
          this.dataModel = {...res?.[0], ...{originalData: this.application}, ...{userInfo: this.currentUser}} ;
                  
            console.log(this.dataModel,"#######################")
          this.missingFields = getMissingFields(this.dataModel);
          resolve();
        },
        error: (err) => reject(err)
      });
    });
  }

  private async initializeFormConfig() {
    this.formOptions = {
      agencies$: this.agencies$,
      creditors$: this.creditors$,
      salesAgents$: this.salesAgents$,
      provinces$: this.provinces$,
      getAgencyName: (agencyId: string) => this.getAgencyNameById(agencyId),
      onUpdate: (model: any, skipRecreate?: boolean) => {
        this.saveChanges(model, skipRecreate);
      },
      hasChanges: (model: any) => Object.keys(this.getChangedFields(this.originalData, model)).length > 0,
      onRecipientsReorder: () => {
        this.recipientsReordered = true;
        this.updateAvailable = true;
      },
      onRecipientsLoad: (recipients: any[]) => {
        this.originalData.recipients = JSON.parse(JSON.stringify(recipients));
      },
    }
    
    await this.mapFormWithCard()
  }

  private loadDropdownOptions() {
		this.getAgencies();
		this.getSalesAgents();
		this.getCreditors();
		this.getProvinces();
	}

  private getProvinces() {
		this.apiService.getModuleField('Deals', 'Province1').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.provinces$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

  private getAgencies() {
		this.zohoService.getAgencies().subscribe({
			next: (res) => {
				this.agencies$.next(res);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

  private getAgencyNameById(agencyId: string): string {
		if (!agencyId) return 'No Agency Selected';
		const agencies = this.agencies$.getValue();
		const agency = agencies.find((a) => a.id === agencyId);
		return agency?.Account_Name || 'Selected Agency';
	}

  private getCreditors() {
		this.apiService.getModuleField('Deals', 'Creditt').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.creditors$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

  private getSalesAgents() {
		this.apiService.getModuleField('Deals', 'Sales_Agent').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.salesAgents$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

  async mapFormWithCard(){
      this.recipientFields = getRecipientList(this.formOptions, this.recipientEnvelopeId, 'col-12 field-sm');
      this.formlyFields = [
        ...getPrimaryAgreementFields(this.formOptions,this.maxCalendarDate, true, 'col-12 field-sm'),
        ...getCoAppAgreementFields(this.formOptions,this.maxCalendarDate, true, 'col-12 field-sm'),
        ...getFundedDetailsAgreementFields(this.formOptions,true, 'col-sm-12 col-lg-6')      
      ]
    }

  onCloseRecipients() {
    this.recipientsClose.emit();
  }

  onScroll(el: HTMLElement) {
      this.isScrolled = el.scrollTop > 0;
  }
  ref:any;
  saveChanges(model, skipRecreate: boolean = false){
    if (this.hasClearedRequiredFields) return;   // Never save over a required value the user cleared
    const changes = this.getChangedFields(this.originalData, model);
    // Two combination if changes && updateAvailable has to be false
    if (Object.keys(changes).length === 0 && !this.updateAvailable) {
      return;
    }
    this.isSavingChanges = true;
    // this.ref = callLoader(this._bottomSheet, 'Saving changes...');
    this.pdfSource = null;
    // Clear the preview pane and step its loader: Deleting -> Creating -> Updating -> Loading
    // Referral edit (skipRecreate) only saves to Zoho, so the current PDF stays on screen
    if (!skipRecreate) {
      this._listenerService.emit('agreementPreviewLoading', true);
    }

    const isTemplateAffectingChange = this.hasTemplateAffectingChange(changes);
    this.handleFieldChange(changes, model, isTemplateAffectingChange, skipRecreate);
  }

  private static readonly META_MODEL_KEYS = ['originalData', 'userInfo', 'envelope_id', 'recipients'];

  private getChangedFields(original: any, updated: any): Record<string, { from: any; to: any }> {
    const changes: Record<string, { from: any; to: any }> = {};
    if (!original || !updated) return changes;

    Object.keys(updated)
      .filter((key) => !AgreementContentUpdateComponent.META_MODEL_KEYS.includes(key))
      .forEach((key) => {
        const originalValue = original[key];
        const updatedValue = updated[key];
        if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
          changes[key] = { from: originalValue, to: updatedValue };
        }
      });

    return changes;
  }

  private hasTemplateAffectingChange(changes: Record<string, { from: any; to: any }>): boolean {
    return Object.keys(changes).some((key) => this.templateChangeData.includes(key.toLowerCase()));
  }

  private handleFieldChange(changes: Record<string, { from: any; to: any }>, model: any, isTemplateAffectingChange: boolean, skipRecreate: boolean = false) {
    // Get updated Deal if changes on Firstname/Middlename/Lastname
    const DealNameValue = (hasRequiredContactChanges(changes))
      ?  { [model['DealName'].zoho_field_name]: [model['FirstName'].value,model['MiddleName'].value,model['LastName'].value].filter(Boolean).join(' ') }
      : null;

    // Sync dataModel.DealName so the recreated document/envelope uses the new changes instead of the initial stale load
    if (DealNameValue) {
      model['DealName'] = { ...model['DealName'], value: DealNameValue[model['DealName'].zoho_field_name] };
    }
    // FullProvince only carries the full name (e.g. "Ontario"); Zoho needs both the
    // full name (Province1) and the 2-letter code (Province) updated together.
    const fullProvinceName = changes['FullProvince']?.to?.value;
    const ProvinceValue = fullProvinceName
      ? { Province: PROVINCE_NAME_TO_CODE[fullProvinceName], Province1: fullProvinceName }
      : null;
    const zohoUpdateData = Object.assign(
      {},
      ...Object.values(changes).map(({ to }: { to: any }) => ({ [to.zoho_field_name]: to.value })),
      (!!DealNameValue ? DealNameValue : {}),
      (!!ProvinceValue ? ProvinceValue : {}),
      { Override_Type: `${this.currentUser['full_name']} (${dayjs().format('YYYY-MM-DD HH:mm:ss')})`}
    )
    
    // Update Zoho Changes
    this.saveZohoChanges(this.application?.id, zohoUpdateData, this.dataModel?.['ContactId'].value, changes, isTemplateAffectingChange, skipRecreate);
  }

  private saveZohoChanges(applicationId: string, zohoUpdateData:any, contactId: string, changes: Record<string, { from: any; to: any }>, isTemplateAffectingChange: boolean, skipRecreate: boolean = false){

    this._docusignService.saveDocusignZohoChanges(applicationId, zohoUpdateData, contactId).pipe(
          finalize(() => {
            // this.ref.dismiss();
          })
        ).subscribe({
      next: (res) => {
        const isSuccess = res?.data?.[0]?.status === 'success';
        if(isSuccess){
          this.originalData = JSON.parse(JSON.stringify(this.dataModel));
          if (!skipRecreate) {  
            this.recreateAgreementDocument(changes, this.currentUser);
          } else {
            this.isSavingChanges = false;
          }
          // Later optimization
          // // if (isTemplateAffectingChange) {
          // //   this.recreateAgreementDocument(changes, this.currentUser);
          // // } else {
          // //   this.updateAgreementDocument(changes, this.currentUser);
          // // }
          this.applicationChange.emit(this.application);
        } else {
          this.isSavingChanges = false;
          // this.ref?.dismiss();
          if (!skipRecreate) this._listenerService.emit('aggreementStaleUpdate', true);   // Nothing was deleted yet, reload the current PDF
        }
      },
      error: (err) => {
        console.error(err);
        this.isSavingChanges = false;
        // this.ref?.dismiss();
        if (!skipRecreate) this._listenerService.emit('aggreementStaleUpdate', true);
      }
    })
  }

  private haveRecipientsReordered(from: Recipient[] = [], to: Recipient[] = []): boolean {
    const identityOf = (r: Recipient) => r.roleName || r.email;
    const fromOrder = from.map(identityOf);
    const toOrder = to.map(identityOf);
    if (fromOrder.length !== toOrder.length) return true;
    return fromOrder.some((id, i) => id !== toOrder[i]);
  }

  private getUpdatedRecipients(recipients: any[]): any[] {
    const dealName = this.dataModel['DealName']?.value;
    return recipients.map((r) => mapRecipientForEnvelopeUpdate(r, dealName));
  }

  private updateAgreementDocument(agreementData: any, userInfo: any){
    const mergedAgreementData = Object.assign(
      {},
      { envelopeId: this.envelopeId },
      ...Object.entries(agreementData).map(([key, { to }]: [string, { to: any }]) =>
        key === 'recipients'
          ? { updatedRecipients: this.getUpdatedRecipients(to as any[]) }
          : { [to.docusign_variable_name]: to }
      )
    );
    this.submitCompositeEnvelopeUpdate(mergedAgreementData, userInfo);
  }

  private submitCompositeEnvelopeUpdate(mergedAgreementData: any, userInfo: any) {
    this._docusignService.updateCompositeEnvelope(mergedAgreementData, userInfo).pipe(
          finalize(() => {
            // this.ref.dismiss();
          })
    ).subscribe({
      next: () => {
        this.updateAvailable = false;
        this.isSavingChanges = false;
        this._listenerService.emit('aggreementStaleUpdate', true);    // Trigger pdf preview reload
      },
      error: (err) => {
        console.error(err);
        this.isSavingChanges = false;
      }
    })
  }

  private recreateAgreementDocument(agreementData: Record<string, { from: any; to: any }>, userInfo: any) {
    const recipients = agreementData['recipients']?.to ?? this.dataModel['recipients'];
    const envelopeData = {
      ...this.dataModel,
      envelopeRecipients: this.getUpdatedRecipients(recipients),
      dbMainEnvelope: {
        application_id: this.application.id,
        applicant_type: 'PRIMARY',
        name: this.dataModel['DealName'].value,
        client_email: this.dataModel['Email'].value,
        via_email: true,
        via_sms: false,
        via_whatsapp: false,
      },
      templateData: { template_row_id: AGREEMENT_TEMPLATE_ROW_ID }
    };
    
    this.deleteEnvelope({ ...this.dataModel, envelope_id: this.envelopeId }, userInfo);
    
    this._docusignService.createCompositeEnvelope(envelopeData, userInfo).pipe(
      finalize(() => {
        // this.ref?.dismiss();
      })
    ).subscribe({
      next: (response) => {
        this.updateAvailable = false;
        this.isSavingChanges = false;
        const newEnvelopeId = response?.['jsonData']?.['docusign_res']?.['envelopeId'];
        this.envelopeId = newEnvelopeId;
        this.dataModel = { ...this.dataModel, envelope_id: newEnvelopeId };
        this._listenerService.emit('aggreementNewUpdate', newEnvelopeId);    // Trigger pdf preview reload  
      },
      error: (err) => {
        console.error(err);
        this.isSavingChanges = false;
      }
    });
  }

  deleteEnvelope(envelopeData:any, userInfo:any){
    this._docusignService.deleteEnvelope({envelopeData: envelopeData, userInfo: userInfo, docusignType: 'AGREEMENT'}).subscribe({
      next: (response)=>{
        
      }
    })
  }
  
  onSend(){
    this.ref = callLoader(this._bottomSheet, 'Sending envelope...');
    this._docusignService.sendEnvelope(
      {
        envelope_id: this.envelopeId, 
        envelopeStatus: 'created', 
        envelopeData: this.dataModel, 
        envelopeIsEdited: false, 
        userInfo: this.currentUser}).pipe(
      finalize(() => {
        this._bottomSheet.dismiss();
      })
    ).subscribe({
      next: (response)=>{
        this._listenerService.emit('agreementIsSent', this.envelopeId);
      }})
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @Input() isEnvelopeUpdating: boolean = false;   // Preview is still updating (and maybe recreating) the envelope

  // No envelopeId while updating, so the list stays on "Loading recipients…" instead of showing the old envelope's
  private get recipientEnvelopeId(): string | null {
    return this.isEnvelopeUpdating ? null : this.envelopeId;
  }

  // The recipient list loads once from the envelopeId it was built with. Opening from the list can recreate the
  // envelope (e.g. co-applicant added/removed), so rebuild the list to load the new envelope's recipients.
  // Before formOptions exist, initializeFormConfig() builds the list with the latest envelopeId anyway.
  private reloadRecipientList(): void {
    if (!this.formOptions) return;
    this.recipientFields = getRecipientList(this.formOptions, this.recipientEnvelopeId, 'col-12 field-sm');
  }

}
